/**
 * IPv4 ACL API Parameter Combinations (Based on create/edit/delete actions):
 *
 * 1. Create (Add):
 *    - Triggered when mode === "add"
 *    - API Payload format: { create: [ { ...rule } ] }
 *    - Schema:
 *      - policy: 'Deny' | 'Permit'
 *      - protocol: 'Any' | 'TCP' | 'UDP' ...etc.
 *      - srcIpAddress: null if IP mode is Any; otherwise an IPv4 string.
 *      - srcPrefixLength: integer from 0 to 32; Any IP uses 0.
 *      - srcPort: -1 if Port mode is Any; number (e.g., 80) if Manual.
 *      - dstIpAddress / dstPrefixLength / dstPort: Destination IP/prefix/port (same logic as source).
 *      - description: Optional string, or null.
 *    - sequenceNo: Omitted; the API assigns the sequence number.
 *    - Note: ID is not included in the create action.
 *
 * 2. Update (Edit):
 *    - Triggered when mode === "edit"
 *    - API Payload format: { update: [ { id, ...rule } ] }
 *    - Schema: Same fields as Create, but with identification fields:
 *      - id: The UUID of the rule
 *
 * 3. Delete (Remove):
 *    - API Payload format: { delete: { ids: [id] } }
 *    - Schema:
 *      - ids: Array of UUIDs to delete.
 */
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIcon } from "assets/images/icons/icn_unchecked.svg";

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

import {
  IPV4_ACL_POLICY_OPTIONS,
  IPV4_ACL_PROTOCOL_OPTIONS,
} from "../util/const";
import { getEndpointValidation } from "../util/func";
import AclEndpointSection from "../components/AclEndpointSection";

const FieldRow = ({ label, children }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minHeight: 46 }}>
      <Typography
        component="div"
        sx={{
          width: 180,
          pt: "6px",
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
};

const getTextFieldSx = (theme, width = 300) => ({
  width,
  "& .MuiOutlinedInput-root": {
    height: 31,
    borderRadius: theme.shape.customBorderRadius[1],
    backgroundColor: theme.palette.primary.white,
  },
  "& .MuiOutlinedInput-input": {
    padding: "4px 10px",
    fontSize: theme.typography.customFontSize[2],
  },
});

const RuleModal = ({
  open,
  mode,
  rule,
  isSaving = false,
  showEditGeneral = true,
  onClose,
  onApply,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(rule);
  const isEdit = mode === "edit";

  useEffect(() => {
    setFormData(rule);
  }, [rule, open]);

  if (!open || !formData) return null;

  const updateField = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateEndpoint = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const sourceValidation = getEndpointValidation(formData.source);
  const destinationValidation = getEndpointValidation(formData.destination);
  const formIsValid = sourceValidation.isValid
    && destinationValidation.isValid;

  const handleApply = () => {
    if (!formIsValid || isSaving) return;

    onApply(formData);
  };

  return (
    <ModalContainer display={open} modalWidth="850px">
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              pl: "10px",
            }}
          >
            {isEdit ? "Edit IPv4 ACL Rule" : "Add New IPv4 ACL Rule"}
          </Typography>
          <Button
            onClick={() => {
              if (!isSaving) onClose();
            }}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": { backgroundColor: theme.palette.primary.transparent },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ px: "35px", pt: "20px" }}>
          <FieldRow label="Policy">
            <RadioGroup
              row
              value={formData.policy}
              onChange={(event) => updateField("policy", event.target.value)}
            >
              {IPV4_ACL_POLICY_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio icon={<UnCheckedIcon />} checkedIcon={<CheckedIcon />} />}
                  label={option.label}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: theme.typography.customFontSize[2],
                      color: theme.palette.primary.greyPrimary,
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FieldRow>

          <FieldRow label="Protocols">
            <MenuSelect
              value={formData.protocol}
              onChange={(option) => updateField("protocol", option.value)}
              options={IPV4_ACL_PROTOCOL_OPTIONS}
              width="300px"
            />
          </FieldRow>

          <AclEndpointSection
            title="Source"
            endpoint={formData.source}
            onChange={(value) => updateEndpoint("source", value)}
          />

          <AclEndpointSection
            title="Destination"
            endpoint={formData.destination}
            onChange={(value) => updateEndpoint("destination", value)}
          />

          <Box sx={{ mt: 2 }}>
            <FieldRow label="Description (Optional)">
              <TextField
                value={formData.comment}
                onChange={(event) => updateField("comment", event.target.value)}
                sx={getTextFieldSx(theme)}
              />
            </FieldRow>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 5, mb: 2 }}>
          <OutlineButton width="120px" height="35px" onClick={onClose} disabled={isSaving}>
            Cancel
          </OutlineButton>
          <SolidButton
            width="120px"
            height="35px"
            onClick={handleApply}
            disabled={!formIsValid || isSaving || (isEdit && !showEditGeneral)}
          >
            {isEdit ? "Save" : "Create"}
          </SolidButton>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export default RuleModal;
